import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { Business, ArrowForward, Person } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { colors } from "./ui/colors";
import { useState, useEffect } from "react";

const options = [
  {
    title: "Individual",
    subtitle: "Perfect for flexible work",
    type: "individual",
    icon: Person,
    description:
      "Find nearby cleaning jobs and set your own schedule with complete flexibility.",
    color: colors.orange,
    cta: "Join as Individual",
  },
  {
    title: "Business",
    subtitle: "Scale quickly and efficiently",
    type: "business",
    icon: Business,
    description:
      "Hire vetted cleaners and manage your cleaning team efficiently.",
    color: colors.yellow,
    cta: "Hire Cleaners",
  },
];

export default function ButtonBaseDemoRoutes({ userInfo }) {
  const navigate = useNavigate();
  const [hoveredOption, setHoveredOption] = useState(null);
  const [cardsVisible, setCardsVisible] = useState([false, false]);
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    const timeouts = [
      setTimeout(() => setCardsVisible((prev) => [true, prev[1]]), 300),
      setTimeout(() => setCardsVisible((prev) => [prev[0], true]), 450),
    ];
    return () => timeouts.forEach(clearTimeout);
  }, []);

  const handleOnClick = (option) => () => {
    if (userInfo) {
      navigate(`/signup/${option}`, { state: { userInfo } });
    } else {
      navigate(`/signup/${option}`);
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: { xs: 3, md: 5 },
        width: "100%",
        maxWidth: "1100px",
        mx: "auto",
        mt: { xs: 2, md: 4 },
      }}
    >
      {options.map((option, index) => {
        const isYellow = option.type === "business"; 
        const mainColor = isYellow ? option.color[700] : option.color.base;
        const gradientText = isYellow
          ? `linear-gradient(135deg, ${option.color[700]}, ${option.color[600]})`
          : `linear-gradient(135deg, ${option.color.base}, ${option.color[400]})`;

        return (
          <ButtonBase
            key={option.title}
            onClick={handleOnClick(option.type)}
            onMouseEnter={() => setHoveredOption(option.type)}
            onMouseLeave={() => setHoveredOption(null)}
            focusRipple
            sx={{
              position: "relative",
              borderRadius: "28px",
              overflow: "hidden",
              minHeight: { xs: "auto", md: 420 },
              p: { xs: 3, md: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "flex-start",
              textAlign: "left",
              backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.9)",
              backdropFilter: "blur(16px)",
              border: `2px solid ${option.color[100]}`,
              boxShadow: `
                0 20px 40px rgba(12,12,12,0.06),
                0 8px 16px rgba(0,0,0,0.08),
                inset 0 1px 0 rgba(255,255,255,0.8)
              `,
              transform: cardsVisible[index]
                ? selectedOption === option.type
                  ? "translateY(-2px) scale(0.98)"
                  : "translateY(0) scale(1)"
                : "translateY(30px) scale(0.94)",
              opacity: cardsVisible[index] ? 1 : 0,
              transition: "all 500ms cubic-bezier(0.16, 1, 0.3, 1)",
              transitionDelay: `${index * 100}ms`,
              "&:hover": {
                transform: selectedOption
                  ? "translateY(-2px) scale(0.98)"
                  : "translateY(-8px) scale(1.02)",
                boxShadow: `
                  0 32px 64px rgba(12,12,12,0.12),
                  0 16px 32px rgba(0,0,0,0.15),
                  inset 0 1px 0 rgba(255,255,255,0.9)
                `,
                borderColor: option.color[200],
                backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.95)",
              },
              ...(selectedOption === option.type && {
                borderColor: option.color[300],
                transform: "translateY(-2px) scale(0.98)",
              }),
            }}
          >
            {/* Icon container */}
            <Box
              className="icon-container"
              sx={{
                width: { xs: 80, md: 100 },
                height: { xs: 80, md: 100 },
                borderRadius: "20px",
                mb: { xs: 2.5, md: 3.5 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${option.color[100]}, ${option.color[50]})`,
                border: `2px solid ${option.color[200]}`,
              }}
            >
              <option.icon
                sx={{
                  fontSize: { xs: 36, md: 48 },
                  color: mainColor,
                }}
              />
            </Box>

            {/* Title and subtitle */}
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${colors.gray.base}, ${colors.gray[400]})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "22px", md: "28px" },
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {option.title}
              </Typography>
              <Typography
                sx={{
                  color: mainColor,
                  fontSize: { xs: "13px", md: "14px" },
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {option.subtitle}
              </Typography>
            </Box>

            <Typography
              sx={{
                color: colors.gray[400],
                fontSize: { xs: "14px", md: "16px" },
                lineHeight: 1.5,
                mb: 3,
              }}
            >
              {option.description}
            </Typography>

            {/* CTA */}
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "auto",
                p: 2,
                borderRadius: "16px",
                background: `linear-gradient(135deg, ${option.color[50]}, rgba(255,255,255,0.8))`,
                border: `1px solid ${option.color[100]}`,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  background: gradientText,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "14px", md: "16px" },
                  letterSpacing: "0.02em",
                }}
              >
                {option.cta}
              </Typography>
              <ArrowForward
                sx={{
                  fontSize: { xs: 20, md: 24 },
                  color: mainColor,
                  opacity: 0.8,
                  transition: "all 400ms cubic-bezier(0.16, 1, 0.3, 1)",
                }}
              />
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
